import React from 'react'
import ReactDOM from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import i18n from './i18n'
import { LanguageProvider } from './contexts/LanguageContext'
import { AuthProvider } from './contexts/AuthContext'
import { ThemeProvider } from './contexts/ThemeContext'
import App from './App.jsx'
import './index.css'

function renderFatalError(message) {
  const root = document.getElementById('root')
  if (!root) return
  const title = i18n.t('layout.errors.fatalLoadingTitle')
  root.innerHTML = `
    <div style="min-height:100vh;display:flex;align-items:center;justify-content:center;padding:16px;background:#f9fafb;font-family:Inter,system-ui,sans-serif;">
      <div style="max-width:560px;width:100%;background:#fff;border:1px solid #fecaca;border-radius:12px;padding:20px;">
        <h1 style="margin:0 0 8px 0;color:#b91c1c;font-size:18px;">${title}</h1>
        <p style="margin:0;color:#374151;font-size:14px;line-height:1.5;">${message}</p>
      </div>
    </div>
  `
}

function setupGlobalErrorFallback() {
  if (typeof window === 'undefined') return

  window.addEventListener('error', (event) => {
    const msg = event?.error?.message || event?.message || i18n.t('layout.errors.initDefaultMessage')
    renderFatalError(msg)
  })

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event?.reason
    const msg =
      (typeof reason === 'string' && reason) ||
      reason?.message ||
      i18n.t('layout.errors.initPromiseMessage')
    renderFatalError(msg)
  })
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutos
    },
  },
})

setupGlobalErrorFallback()

class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(error) {
    console.error('Erro fatal na aplicação:', error)
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
          <div className="max-w-md w-full bg-white border border-red-200 rounded-xl p-6 text-center">
            <h1 className="text-lg font-semibold text-red-700">{i18n.t('layout.errors.boundaryTitle')}</h1>
            <p className="mt-2 text-sm text-gray-600">
              {i18n.t('layout.errors.boundaryBody')}
            </p>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <AppErrorBoundary>
        <LanguageProvider>
          <AuthProvider>
            <ThemeProvider>
              <App />
            </ThemeProvider>
          </AuthProvider>
        </LanguageProvider>
      </AppErrorBoundary>
    </QueryClientProvider>
  </React.StrictMode>,
)
